import { PayinState } from '@/features/payin/payinSlice';
import paymentTextContent from '@/features/paymentSelect/paymentTextContent';

interface InitialTextProps {
  isPayIn: boolean,
  providerGroup: string,
  isAARN: boolean,
  currentJourney: string
}

interface SupplementaryTextProps {
  isPayIn: boolean,
  payinState: PayinState,
  isAARN: boolean,
  postalRewards: boolean
}
/**
 * Gets the audience text for a payin activity
 * @param audience The audience type from payin state
 * @returns The formatted audience text
 */
export const getActivityTitleAudienceText = (audience?: string): string => {
  switch (audience) {
    case 'school':
      return ' for a school, early years setting or youth group';
    case 'work':
      return ' for a workplace';
    case 'public':
      return ' for yourself or your community';
    default:
      return '';
  }
};

/**
 * Generates the payment description text for the PageHeader component
 * @param isPayIn Whether this is a payin payment
 * @param payinState The payin state (required if isPayIn is true)
 * @param providerGroup The provider group (single or monthly)
 * @param isAARN Whether this is an Adopt A Red Nose journey or not
 * @param currentJourney The type of payment journey currently in progress
 * @returns The formatted payment description text without amount and currency symbol
 */

export const getPageHeaderInitialText = ({
  providerGroup = '',
  isPayIn = false,
  isAARN = false,
  currentJourney = '',
}:Partial<InitialTextProps>): string => {
  const {
    initialTextSingle, initialTextDirectPayment, initialTextMonthly, initialTextPayin
  } = paymentTextContent;

  if (currentJourney === 'direct') {
    return initialTextDirectPayment;
  }

  if (isPayIn) {
    return initialTextPayin;
  }

  if (isAARN) {
    return paymentTextContent.initialTextMonthlyAARN;
  }

  if (providerGroup === 'monthly') {
    return initialTextMonthly;
  }

  return initialTextSingle;
};

/**
 * Generates the supplementary text for payin payments
 * @param isPayIn Whether this is a payin payment
 * @param payinState The payin state (required if isPayIn is true)
 * @param isAARN Whether this is an Adopt A Red Nose journey or not
 * @param postalRewards Whether the user has opted in for postal awards (AARN only, currently)
 * @returns The supplementary text to display after the amount
 */
export const getPageHeaderSupplementaryText = ({
  isPayIn = false,
  payinState = undefined,
  isAARN = false,
  postalRewards = false
}:Partial<SupplementaryTextProps>): string => {
  if (isPayIn && payinState) {
    return `from fundraising${payinState?.audience ? getActivityTitleAudienceText(payinState.audience) : ''}`;
  }

  if (isAARN) {
    return postalRewards
      ? paymentTextContent.supplementaryTextMonthlyAARNOptedIn
      : paymentTextContent.supplementaryTextMonthlyAARNOptedOut;
  }

  return '';
};
